import { URL_NAVS } from '@/constants';
import {
  platformIcons as platformIconsSelector,
  strComplaintsAvailable,
} from '@/data/data-selectors';
import { setFeaturedDeckardId } from '@/map/map-actions';
import { DefaultExceptionPlaceholder } from '@/placeholders';
import { PropertyDetailsSkeleton } from '@/placeholders/skeleton-placeholders/property-details-skeleton';
import { detectDefaultOnlinePlatform } from '@/utils/data-utils';
import { useComplaints } from '@/utils/hooks/data/useComplaints';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { useOptionalPropertyDeckardId } from '@/utils/hooks/usePropertyDeckardId';
import { usePlaceSpecificUrlFactory } from '@/utils/place-detection';
import { smartEncodeURIComponent } from '@/utils/uri-utils';
import { ImageSlider } from '@/widgets/components/property-image-carousel/image-slider';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DetailsInfo } from './details-info';
import { DetailsVis } from './details-vis';
import { DetailsWrapper, Wrapper } from './property-details-content-styles';
import { PropertyNotFound } from './property-details-not-found';

const initialListingPlatform = 'all';

/**
 * This is what we put inside the modal where the property details is displayed
 */
export function PropertyDetailsContent(): JSX.Element | null {
  const history = useHistory();
  const dispatch = useDispatch();

  const platformIcons = useSelector(platformIconsSelector);
  const isComplaintsEnabled = useSelector(strComplaintsAvailable);

  const { propertyDeckardId } = useOptionalPropertyDeckardId();
  const getPlaceSpecificUrl = usePlaceSpecificUrlFactory();

  const {
    currentProperty,
    loading: propertyLoading,
    notFound: propertyNotFound,
    hasError,
  } = usePropertyDetails();

  const { complaints, loading: complaintsLoading } = useComplaints();

  // When the current platform changes, we need to update property info snapshot
  const [currentPlatform, setCurrentPlatform] = useState<string>(
    initialListingPlatform
  );

  const onMapClick = useCallback(() => {
    if (!propertyDeckardId) return;

    dispatch(setFeaturedDeckardId(propertyDeckardId));

    const encodedPropertyDeckardId = smartEncodeURIComponent(propertyDeckardId);

    history.push(
      getPlaceSpecificUrl(
        `/${URL_NAVS.home}?featured=${encodedPropertyDeckardId}`
      )
    );
  }, [dispatch, getPlaceSpecificUrl, history, propertyDeckardId]);

  const defaultVendor = useMemo(
    () => detectDefaultOnlinePlatform(currentProperty?.imageUrls),
    [currentProperty]
  );

  if (propertyLoading || complaintsLoading) {
    return <PropertyDetailsSkeleton />;
  }

  if (propertyNotFound) {
    return <PropertyNotFound />;
  }

  if (hasError) {
    return <DefaultExceptionPlaceholder />;
  }

  return (
    // using deckard id as key to force re-render
    <Wrapper
      key={propertyDeckardId}
      data-testid="property-details-overview-view"
    >
      {!!defaultVendor && (
        <ImageSlider
          imageURLs={currentProperty?.imageUrls?.[defaultVendor]}
          propertyAddress={currentProperty.address}
        />
      )}

      <DetailsWrapper>
        <DetailsInfo
          currentPlatform={currentPlatform}
          complaints={complaints}
          isComplaintsEnabled={isComplaintsEnabled}
          setCurrentPlatform={setCurrentPlatform}
          data={currentProperty}
          onMapClick={onMapClick}
          platformIcons={platformIcons}
        />

        <div className="max-w-unset min-w-[25px] w-full" />

        <DetailsVis propertyData={currentProperty} />
      </DetailsWrapper>
    </Wrapper>
  );
}
